<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { useImpersonation, uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import { AdminHostingSiteDetail, type AdminHostingSiteDetail as AdminHostingSiteDetailT } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import { formatBytes } from '@/utils'

defineOptions({ name: 'AdminHostingDetail' })

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const notifyStore = useNotifyStore()
const { startImpersonation } = useImpersonation()

const siteId = computed(() => Number(route.params.id))
const loading = ref(true)
const actionLoading = ref(false)
const activeTab = ref('overview')
const detail = ref<AdminHostingSiteDetailT | null>(null)

const showDeleteDialog = ref(false)
const deleteConfirmName = ref('')
const rotatedPassword = ref<string | null>(null)

const tabs = [
  { value: 'overview', label: 'dashboard.admin.hosting.detail.tabs.overview' },
  { value: 'domains', label: 'dashboard.admin.hosting.detail.tabs.domains' },
  { value: 'databases', label: 'dashboard.admin.hosting.detail.tabs.databases' },
  { value: 'actions', label: 'dashboard.admin.hosting.detail.tabs.actions' }
]

async function fetchDetail() {
  loading.value = true
  try {
    const res = await useAxios<null, unknown>('GET', `/admin/hosting/sites/${siteId.value}`, true)
    if (res && res.status === STATUS_CODES.OK) {
      const parsed = AdminHostingSiteDetail.safeParse(res.data)
      if (parsed.success) {
        detail.value = parsed.data
      } else {
        console.error('[AdminHostingDetail] parse failed', parsed.error.issues)
      }
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminHostingDetail' })
  } finally {
    loading.value = false
  }
}

async function runAction(path: string, successKey: string): Promise<boolean> {
  actionLoading.value = true
  try {
    const res = await useAxios<null, unknown>('POST', `/admin/hosting/sites/${siteId.value}/${path}`, true)
    if (res && (res.status === STATUS_CODES.OK || res.status === STATUS_CODES.NO_CONTENT)) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t(successKey),
        message: detail.value?.name ?? ''
      })
      return true
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: `AdminHostingDetail:${path}` })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(err)
    })
  } finally {
    actionLoading.value = false
  }
  return false
}

async function restart() {
  if (await runAction('restart', 'dashboard.admin.hosting.detail.notify.restartSuccess')) {
    await fetchDetail()
  }
}

async function reconcile() {
  if (await runAction('reconcile', 'dashboard.admin.hosting.detail.notify.reconcileSuccess')) {
    await fetchDetail()
  }
}

async function rotateSFTP() {
  actionLoading.value = true
  try {
    const res = await useAxios<null, { password: string }>('POST', `/admin/hosting/sites/${siteId.value}/rotate-sftp`, true)
    if (res && res.status === STATUS_CODES.OK && res.data?.password) {
      rotatedPassword.value = res.data.password
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.hosting.detail.notify.rotateSuccess'),
        message: detail.value?.name ?? ''
      })
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminHostingDetail:rotate-sftp' })
  } finally {
    actionLoading.value = false
  }
}

async function deleteSite() {
  if (!detail.value || deleteConfirmName.value !== detail.value.name) return
  actionLoading.value = true
  try {
    const res = await useAxios<null, null>('DELETE', `/admin/hosting/sites/${siteId.value}`, true)
    if (res && (res.status === STATUS_CODES.NO_CONTENT || res.status === STATUS_CODES.OK)) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.hosting.detail.notify.deleteSuccess'),
        message: detail.value.name
      })
      showDeleteDialog.value = false
      router.push({ name: 'dashboardAdminHosting' })
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminHostingDetail:delete' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(err)
    })
  } finally {
    actionLoading.value = false
  }
}

async function impersonateOwner() {
  if (!detail.value) return
  await startImpersonation(detail.value.account_id)
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-'
  return DateTime.fromISO(iso).toFormat('dd.MM.yyyy HH:mm')
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'active': return 'status-active'
    case 'provisioning': return 'status-provisioning'
    case 'failed': return 'status-failed'
    case 'suspended': return 'status-suspended'
    case 'deprovisioned': return 'status-deprovisioned'
    default: return ''
  }
}

onMounted(fetchDetail)
</script>

<template>
  <BaseDashboardPage
    :title="detail?.name ?? t('dashboard.admin.hosting.detail.title')"
    :subtitle="t('dashboard.admin.hosting.detail.subtitle')"
    icon="mdi-web-box"
    icon-color="cyan"
  >
    <template #actions>
      <button class="btn-text" @click="router.push({ name: 'dashboardAdminHosting' })">
        <v-icon icon="mdi-arrow-left" size="16" />
        {{ t('common.back') }}
      </button>
    </template>

    <div v-if="loading" class="state-box">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <div v-else-if="!detail" class="state-box">
      <v-icon icon="mdi-alert-circle-outline" size="48" class="empty-icon" />
      <p class="empty-title">{{ t('dashboard.admin.hosting.detail.notFound') }}</p>
    </div>

    <div v-else>
      <!-- Summary header -->
      <div class="summary-card">
        <div class="summary-top">
          <div>
            <div class="site-name mono">{{ detail.name }}</div>
            <div class="site-meta">
              <span class="status-chip" :class="getStatusClass(detail.status)">{{ detail.status }}</span>
              <span v-if="detail.legacy_vhost_id" class="legacy-badge">legacy: {{ detail.legacy_vhost_id }}</span>
            </div>
          </div>
          <button class="btn-primary" :disabled="actionLoading" @click="impersonateOwner">
            <v-icon icon="mdi-account-switch" size="16" />
            {{ t('dashboard.admin.hosting.detail.actions.impersonate') }}
          </button>
        </div>
        <div class="summary-grid">
          <div class="summary-item">
            <span class="label">{{ t('dashboard.admin.hosting.detail.fields.owner') }}</span>
            <span class="value">{{ detail.account_email }} <span class="muted">#{{ detail.account_id }}</span></span>
          </div>
          <div class="summary-item">
            <span class="label">{{ t('dashboard.admin.hosting.detail.fields.product') }}</span>
            <span class="value">{{ detail.product_slug }}</span>
          </div>
          <div class="summary-item">
            <span class="label">{{ t('dashboard.admin.hosting.detail.fields.runtime') }}</span>
            <span class="value">{{ detail.runtime }} / {{ detail.web_server }}</span>
          </div>
          <div class="summary-item">
            <span class="label">{{ t('dashboard.admin.hosting.detail.fields.quota') }}</span>
            <span class="value">
              {{ formatBytes(detail.bytes_used) }} / {{ formatBytes(detail.quota_bytes) }}
            </span>
          </div>
          <div class="summary-item">
            <span class="label">{{ t('dashboard.admin.hosting.detail.fields.sftp') }}</span>
            <span class="value mono small">{{ detail.sftp_username }}</span>
          </div>
          <div class="summary-item">
            <span class="label">{{ t('dashboard.admin.hosting.detail.fields.created') }}</span>
            <span class="value">{{ formatDate(detail.created_at) }}</span>
          </div>
          <div v-if="detail.stripe_subscription_id" class="summary-item">
            <span class="label">Stripe</span>
            <span class="value mono small">{{ detail.stripe_subscription_id }}</span>
          </div>
          <div v-if="detail.trial_end" class="summary-item">
            <span class="label">{{ t('dashboard.admin.hosting.detail.fields.trialEnd') }}</span>
            <span class="value">{{ formatDate(detail.trial_end) }}</span>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          class="tab"
          :class="{ active: activeTab === tab.value }"
          @click="activeTab = tab.value"
        >
          {{ t(tab.label) }}
        </button>
      </div>

      <!-- Overview (K8s status) -->
      <div v-if="activeTab === 'overview'" class="section-card">
        <h3>{{ t('dashboard.admin.hosting.detail.k8sStatus') }}</h3>
        <div v-if="detail.k8s_status" class="kv-grid">
          <div><span class="label">Phase</span><span class="value">{{ detail.k8s_status.phase }}</span></div>
          <div><span class="label">Ready</span><span class="value">{{ detail.k8s_status.ready ? '✓' : '✗' }}</span></div>
          <div><span class="label">Replicas</span><span class="value">{{ detail.k8s_status.ready_replicas }} / {{ detail.k8s_status.replicas }}</span></div>
          <div v-if="detail.k8s_status.message"><span class="label">Message</span><span class="value">{{ detail.k8s_status.message }}</span></div>
        </div>
        <p v-else class="muted small">{{ t('dashboard.admin.hosting.detail.noK8sStatus') }}</p>
      </div>

      <!-- Domains -->
      <div v-if="activeTab === 'domains'" class="section-card">
        <h3>{{ t('dashboard.admin.hosting.detail.tabs.domains') }}</h3>
        <ul v-if="detail.domains.length > 0" class="list">
          <li v-for="d in detail.domains" :key="d.id" class="mono">{{ d.name }}</li>
        </ul>
        <p v-else class="muted small">{{ t('dashboard.admin.hosting.detail.noDomains') }}</p>
      </div>

      <!-- Databases -->
      <div v-if="activeTab === 'databases'" class="section-card">
        <h3>{{ t('dashboard.admin.hosting.detail.tabs.databases') }}</h3>
        <div v-if="detail.databases && detail.databases.length > 0" class="data-table">
          <div class="db-header">
            <span>Name</span><span>Type</span><span>Ready</span><span>Host</span><span>User</span>
          </div>
          <div v-for="db in detail.databases" :key="db.name" class="db-row">
            <span class="mono">{{ db.name }}</span>
            <span>{{ db.type }}</span>
            <span>{{ db.ready ? '✓' : '⏳' }}</span>
            <span class="mono small">{{ db.host }}:{{ db.port }}</span>
            <span class="mono small">{{ db.username }}</span>
          </div>
        </div>
        <p v-else class="muted small">{{ t('dashboard.admin.hosting.detail.noDatabases') }}</p>
      </div>

      <!-- Actions -->
      <div v-if="activeTab === 'actions'" class="section-card">
        <h3>{{ t('dashboard.admin.hosting.detail.tabs.actions') }}</h3>
        <div class="action-list">
          <div class="action-row">
            <div>
              <div class="action-title">{{ t('dashboard.admin.hosting.detail.actions.restart') }}</div>
              <div class="action-desc">{{ t('dashboard.admin.hosting.detail.actions.restartDesc') }}</div>
            </div>
            <button class="btn-secondary" :disabled="actionLoading" @click="restart">
              <v-icon icon="mdi-restart" size="16" /> {{ t('dashboard.admin.hosting.detail.actions.restart') }}
            </button>
          </div>
          <div class="action-row">
            <div>
              <div class="action-title">{{ t('dashboard.admin.hosting.detail.actions.reconcile') }}</div>
              <div class="action-desc">{{ t('dashboard.admin.hosting.detail.actions.reconcileDesc') }}</div>
            </div>
            <button class="btn-secondary" :disabled="actionLoading" @click="reconcile">
              <v-icon icon="mdi-sync" size="16" /> {{ t('dashboard.admin.hosting.detail.actions.reconcile') }}
            </button>
          </div>
          <div class="action-row">
            <div>
              <div class="action-title">{{ t('dashboard.admin.hosting.detail.actions.rotateSftp') }}</div>
              <div class="action-desc">{{ t('dashboard.admin.hosting.detail.actions.rotateSftpDesc') }}</div>
              <div v-if="rotatedPassword" class="rotated-password">
                {{ t('dashboard.admin.hosting.detail.actions.newPassword') }}:
                <span class="mono">{{ rotatedPassword }}</span>
              </div>
            </div>
            <button class="btn-secondary" :disabled="actionLoading" @click="rotateSFTP">
              <v-icon icon="mdi-key-change" size="16" /> {{ t('dashboard.admin.hosting.detail.actions.rotateSftp') }}
            </button>
          </div>
          <div class="action-row danger">
            <div>
              <div class="action-title">{{ t('dashboard.admin.hosting.detail.actions.delete') }}</div>
              <div class="action-desc">{{ t('dashboard.admin.hosting.detail.actions.deleteDesc') }}</div>
            </div>
            <button class="btn-danger" :disabled="actionLoading || detail.status === 'deprovisioned'" @click="showDeleteDialog = true">
              <v-icon icon="mdi-delete-outline" size="16" /> {{ t('dashboard.admin.hosting.detail.actions.delete') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <v-dialog v-model="showDeleteDialog" max-width="500">
      <div class="dialog-card">
        <h3>{{ t('dashboard.admin.hosting.detail.actions.deleteConfirmTitle') }}</h3>
        <p>{{ t('dashboard.admin.hosting.detail.actions.deleteConfirmBody', { name: detail?.name ?? '' }) }}</p>
        <input
          v-model="deleteConfirmName"
          type="text"
          class="confirm-input"
          placeholder="site name"
        />
        <div class="dialog-actions">
          <button class="btn-secondary" @click="showDeleteDialog = false; deleteConfirmName = ''">
            {{ t('common.cancel') }}
          </button>
          <button
            class="btn-danger"
            :disabled="actionLoading || deleteConfirmName !== detail?.name"
            @click="deleteSite"
          >
            {{ t('dashboard.admin.hosting.detail.actions.delete') }}
          </button>
        </div>
      </div>
    </v-dialog>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.btn-text, .btn-primary, .btn-secondary, .btn-danger {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px; border-radius: 8px; font-size: 0.85rem; font-weight: 500;
  cursor: pointer; transition: all 0.2s ease;
}
.btn-text { background: rgba(120, 163, 255, 0.08); border: 1px solid rgba(120, 163, 255, 0.15); color: rgba(200, 218, 255, 0.8); padding: 6px 12px; font-size: 0.82rem; }
.btn-text:hover { background: rgba(120, 163, 255, 0.15); }
.btn-primary { background: rgba(0, 176, 255, 0.15); border: 1px solid rgba(0, 176, 255, 0.3); color: #00B0FF; }
.btn-primary:hover:not(:disabled) { background: rgba(0, 176, 255, 0.25); }
.btn-secondary { background: rgba(120, 163, 255, 0.08); border: 1px solid rgba(120, 163, 255, 0.15); color: rgba(200, 218, 255, 0.8); }
.btn-secondary:hover:not(:disabled) { background: rgba(120, 163, 255, 0.15); }
.btn-danger { background: rgba(244, 67, 54, 0.1); border: 1px solid rgba(244, 67, 54, 0.3); color: #ef5350; }
.btn-danger:hover:not(:disabled) { background: rgba(244, 67, 54, 0.2); }
.btn-primary:disabled, .btn-secondary:disabled, .btn-danger:disabled { opacity: 0.4; cursor: not-allowed; }

.summary-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 24px;
}
.summary-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; gap: 16px; flex-wrap: wrap; }
.site-name { font-size: 1.2rem; font-weight: 600; color: rgba(255, 255, 255, 0.95); margin-bottom: 6px; }
.site-meta { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px 20px;
}
.summary-item { display: flex; flex-direction: column; gap: 2px; }
.label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; color: rgba(200, 218, 255, 0.5); font-weight: 600; }
.value { font-size: 0.9rem; color: rgba(255, 255, 255, 0.85); }
.muted { color: rgba(200, 218, 255, 0.5); }
.small { font-size: 0.78rem; }
.mono { font-family: 'JetBrains Mono', 'Fira Code', monospace; }

.status-chip { padding: 2px 8px; border-radius: 6px; font-size: 0.78rem; font-weight: 500; }
.status-active { background: rgba(129, 199, 132, 0.1); border: 1px solid rgba(129, 199, 132, 0.3); color: #81c784; }
.status-provisioning { background: rgba(255, 183, 77, 0.1); border: 1px solid rgba(255, 183, 77, 0.3); color: #ffb74d; }
.status-failed { background: rgba(244, 67, 54, 0.1); border: 1px solid rgba(244, 67, 54, 0.3); color: #ef5350; }
.status-suspended { background: rgba(255, 152, 0, 0.1); border: 1px solid rgba(255, 152, 0, 0.3); color: #ff9800; }
.status-deprovisioned { background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); color: rgba(200, 218, 255, 0.5); }
.legacy-badge {
  padding: 2px 8px; border-radius: 4px;
  background: rgba(186, 104, 200, 0.15); border: 1px solid rgba(186, 104, 200, 0.3);
  color: #ba68c8; font-size: 0.7rem; font-weight: 600; text-transform: uppercase;
}

.tabs { display: flex; gap: 4px; margin-bottom: 16px; border-bottom: 1px solid rgba(120, 163, 255, 0.1); }
.tab {
  padding: 8px 16px; background: transparent; border: none; border-bottom: 2px solid transparent;
  color: rgba(200, 218, 255, 0.6); font-size: 0.85rem; font-weight: 500;
  cursor: pointer; transition: all 0.2s ease;
}
.tab:hover { color: rgba(255, 255, 255, 0.85); }
.tab.active { color: #00B0FF; border-bottom-color: #00B0FF; }

.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 20px 24px;
  h3 { margin: 0 0 16px; font-size: 1rem; font-weight: 600; color: rgba(255, 255, 255, 0.9); }
}
.kv-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
.kv-grid > div { display: flex; flex-direction: column; gap: 2px; }

.list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
.list li { padding: 8px 12px; background: rgba(120, 163, 255, 0.04); border-radius: 6px; font-size: 0.88rem; }

.data-table { overflow-x: auto; }

.data-table .db-header, .data-table .db-row {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr 0.6fr 1.4fr 1fr;
  gap: 12px;
  padding: 8px 12px;
  font-size: 0.85rem;
  min-width: 760px;
}
.db-header { font-size: 0.72rem; font-weight: 600; color: rgba(200, 218, 255, 0.5); text-transform: uppercase; border-bottom: 1px solid rgba(120, 163, 255, 0.1); }
.db-row { border-bottom: 1px solid rgba(120, 163, 255, 0.05); }
.db-row:last-child { border-bottom: none; }

.action-list { display: flex; flex-direction: column; gap: 12px; }
.action-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px; gap: 16px;
  background: rgba(120, 163, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 10px;
  &.danger { background: rgba(244, 67, 54, 0.04); border-color: rgba(244, 67, 54, 0.15); }
}
.action-title { font-weight: 500; color: rgba(255, 255, 255, 0.9); margin-bottom: 2px; }
.action-desc { font-size: 0.82rem; color: rgba(200, 218, 255, 0.6); }
.rotated-password {
  margin-top: 8px;
  padding: 8px;
  background: rgba(0, 176, 255, 0.08);
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 6px;
  font-size: 0.85rem;
}

.state-box { display: flex; flex-direction: column; align-items: center; padding: 48px 24px; text-align: center; }
.empty-icon { color: rgba(200, 218, 255, 0.25); margin-bottom: 12px; }
.empty-title { font-size: 1rem; font-weight: 500; color: rgba(200, 218, 255, 0.6); margin: 0; }

.dialog-card {
  background: #1a1d28;
  border-radius: 16px;
  padding: 24px;
  h3 { margin: 0 0 12px; color: rgba(255, 255, 255, 0.95); }
  p { color: rgba(200, 218, 255, 0.75); margin-bottom: 16px; }
}
.confirm-input {
  width: 100%;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'JetBrains Mono', monospace;
  margin-bottom: 16px;
}
.dialog-actions { display: flex; justify-content: flex-end; gap: 8px; }
</style>
